import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Camera, ExternalLink } from 'lucide-react';

// Behold.so Feed ID - Replace with your actual feed ID from behold.so
const BEHOLD_FEED_ID = process.env.REACT_APP_BEHOLD_FEED_ID || '';

const EventGallery = () => {
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback static images when no Behold feed is configured
  // Memoize the array so its reference stays stable across renders
  const fallbackImages = useMemo(
    () => [
      {
        id: '1',
        mediaUrl:
          'https://images.unsplash.com/photo-1702302014005-ac80ec0c7638?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-2 md:row-span-2',
      },
      {
        id: '2',
        mediaUrl:
          'https://images.unsplash.com/photo-1543503452-3766f398e2c9?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-1 md:row-span-1',
      },
      {
        id: '3',
        mediaUrl:
          'https://images.unsplash.com/photo-1580775174971-149b403a7e0b?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-1 md:row-span-1',
      },
      {
        id: '4',
        mediaUrl:
          'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-2 md:row-span-1',
      },
      {
        id: '5',
        mediaUrl:
          'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-1 md:row-span-1',
      },
      {
        id: '6',
        mediaUrl:
          'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?q=80&w=2070&auto=format&fit=crop',
        permalink: '#',
        span: 'md:col-span-1 md:row-span-1',
      },
    ],
    [],
  );

  // Grid span patterns for Instagram posts (memoized)
  const spanPatterns = useMemo(
    () => [
      'md:col-span-2 md:row-span-2',
      'md:col-span-1 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-2 md:row-span-1',
      'md:col-span-1 md:row-span-1',
      'md:col-span-1 md:row-span-1',
    ],
    [],
  );

  const fetchInstagramFeed = useCallback(
    async () => {
      if (!BEHOLD_FEED_ID) {
        // No feed ID configured, use fallback images
        setInstagramPosts(fallbackImages);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `https://feeds.behold.so/${BEHOLD_FEED_ID}`,
        );

        if (!response.ok) {
          throw new Error('Failed to fetch Instagram feed');
        }

        const data = await response.json();

        // Map Behold posts to our format with grid spans
        const posts = (data.posts || [])
          .slice(0, 6)
          .map((post, index) => ({
            id: post.id,
            mediaUrl:
              post.sizes?.large?.mediaUrl || post.mediaUrl,
            permalink: post.permalink,
            caption: post.prunedCaption || post.caption,
            timestamp: post.timestamp,
            mediaType: post.mediaType,
            span: spanPatterns[index % spanPatterns.length],
          }));

        setInstagramPosts(
          posts.length > 0 ? posts : fallbackImages,
        );
        setError(null);
      } catch (err) {
        console.error('Error fetching Instagram feed:', err);
        setError(err.message);
        setInstagramPosts(fallbackImages);
      } finally {
        setLoading(false);
      }
    },
    [fallbackImages, spanPatterns],
  );

  useEffect(() => {
    fetchInstagramFeed();

    // Refresh every 5 minutes
    const interval = setInterval(
      fetchInstagramFeed,
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [fetchInstagramFeed]);

  const isInstagramConnected =
    BEHOLD_FEED_ID &&
    instagramPosts.length > 0 &&
    !error;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[#050505] noise-bg"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#00F0FF] text-sm font-medium tracking-widest uppercase mb-4 block">
            {isInstagramConnected
              ? 'Follow Our Journey'
              : 'Our Events'}
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">
              LIVE
            </span>{' '}
            <span className="text-gradient">
              SLUSH'D
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {isInstagramConnected
              ? "Fresh from our Instagram feed - see what we've been up to!"
              : 'From massive festivals to intimate gatherings, we bring the chill everywhere.'}
          </p>

          {/* Instagram Badge */}
          {isInstagramConnected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white text-sm font-medium"
            >
              <Instagram className="w-4 h-4" />
              <span>Live from Instagram</span>
            </motion.div>
          )}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[200px] md:auto-rows-[250px]">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-2xl bg-white/5 animate-pulse ${spanPatterns[index]}`}
              />
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[200px] md:auto-rows-[250px]">
            {instagramPosts.map((post, index) => (
              <motion.a
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                }}
                className={`relative group overflow-hidden rounded-2xl cursor-pointer ${post.span}`}
              >
                {/* Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{
                    backgroundImage: `url(${post.mediaUrl})`,
                  }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />

                {/* Border Glow on Hover */}
                <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-[#CCFF00]/50 transition-colors duration-300" />

                {/* Caption on Hover (for Instagram posts) */}
                {post.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-sm line-clamp-2">
                      {post.caption}
                    </p>
                  </div>
                )}

                {/* Icon Indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 rounded-full bg-[#CCFF00] flex items-center justify-center">
                    {isInstagramConnected ? (
                      <ExternalLink className="w-5 h-5 text-black" />
                    ) : (
                      <Camera className="w-5 h-5 text-black" />
                    )}
                  </div>
                </div>

                {/* Video indicator */}
                {post.mediaType === 'VIDEO' && (
                  <div className="absolute top-4 left-4">
                    <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent ml-1" />
                    </div>
                  </div>
                )}
              </motion.a>
            ))}
          </div>
        )}

        {/* Instagram Follow CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <a
            href="#contact"
            data-testid="gallery-cta"
            className="inline-flex items-center gap-2 text-[#CCFF00] hover:text-white transition-colors duration-300 text-sm uppercase tracking-widest font-medium"
          >
            <span>Want us at your event?</span>
            <span className="text-lg">→</span>
          </a>

          {/* Instagram Follow Button */}
          <a
            href="https://instagram.com/tryslushd"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white text-sm uppercase tracking-widest font-medium hover:opacity-90 transition-opacity"
          >
            <Instagram className="w-5 h-5" />
            <span>Follow Us</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default EventGallery;
